/**
 * NodeInspector — the per-process editor shown in Build mode when a node is
 * selected. Consolidates what used to be two separate panels: the left "specs"
 * box (name / duration / temperature / equipment / source) and the right
 * "inventory" tab (outputs with inline price editor + the LCI inputs table).
 *
 * Pure presentational: all state and handlers come from WorkspaceContext.
 */

import React from 'react';
import LCIInputsTable from '../LCIInputsTable';
import DatabaseModeToggle from '../DatabaseModeToggle';
import { useWorkspace } from './WorkspaceContext';
import { fmt3 } from './workspaceFormat';
import { useProjectStore } from '../../store/projectStore';
import { getActiveImpactSource, setActiveImpactSource } from '../../services/impactSource';

export default function NodeInspector() {
  const {
    activeProcess,
    editingSpecName, setEditingSpecName, specNameDraft, setSpecNameDraft,
    handleRenameProcess, setSelectedProcess,
    isDemoBuild, setTraciDbMode, useNoisyDb,
    lciInputs, handleUpdateProcess, handleUpdateProcessFields, handleDeleteProcess,
    editingOutputPrice, setEditingOutputPrice,
  } = useWorkspace();

  const impactSource = useProjectStore((s) => getActiveImpactSource(s.project));
  // Switching the lookup's dataset switches the project's, as the Results page does.
  const switchImpactSource = (source) => {
    const { project, updateProjectTable } = useProjectStore.getState();
    if (!project || source === getActiveImpactSource(project)) return;
    updateProjectTable(setActiveImpactSource(project, source));
  };

  if (!activeProcess) return null;

  const outputs = activeProcess.products || activeProcess.output_materials || [];
  const hasOutputs = outputs.length > 0;

  // Commit helpers (commit on blur / Enter — same pattern as the price editor).
  const commitField = (field, value) => {
    if (handleUpdateProcessFields) handleUpdateProcessFields(activeProcess.id, { [field]: value });
  };
  const commitParam = (field, value) => {
    const n = Number(value);
    if (handleUpdateProcess) handleUpdateProcess(activeProcess.id, { [field]: Number.isFinite(n) ? n : 0 });
  };
  const blurOnEnter = (e) => { if (e.key === 'Enter') e.currentTarget.blur(); };

  return (
    <div className="node-inspector">
      {/* Header: name (inline-renamable) + id */}
      <div className="specs-header">
        {editingSpecName ? (
          <input
            className="specs-process-name"
            value={specNameDraft}
            onChange={e => setSpecNameDraft(e.target.value)}
            onBlur={() => { handleRenameProcess(activeProcess.id, specNameDraft); setEditingSpecName(false); }}
            onKeyDown={e => {
              if (e.key === 'Enter') { handleRenameProcess(activeProcess.id, specNameDraft); setEditingSpecName(false); }
              else if (e.key === 'Escape') setEditingSpecName(false);
            }}
            autoFocus
            style={{ fontSize: '13px', fontWeight: 600, border: '1px solid #1a73e8', borderRadius: 3, padding: '1px 4px', background: '#fff', outline: 'none', fontFamily: 'inherit', color: '#1a1a2e', minWidth: 0, flex: 1 }}
          />
        ) : (
          <span
            className="specs-process-name"
            title="Double-click to rename"
            style={{ cursor: 'text' }}
            onDoubleClick={() => { setSpecNameDraft(activeProcess.name?.replace(/_/g, ' ') || ''); setEditingSpecName(true); }}
          >
            {activeProcess.name?.replace(/_/g, ' ')}
          </span>
        )}
        <span className="process-id-badge">{activeProcess.id}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button
            onClick={() => handleDeleteProcess && handleDeleteProcess(activeProcess.id)}
            title="Delete this step"
            style={{ background: 'none', border: '1px solid #f3c2bd', borderRadius: 4, padding: '2px 8px', fontSize: '11px', color: '#c0392b', cursor: 'pointer' }}
          >Delete</button>
          <button
            onClick={() => setSelectedProcess(null)}
            title="Close inspector"
            style={{ background: 'none', border: '1px solid #dee2e6', borderRadius: 4, padding: '2px 8px', fontSize: '11px', color: '#666', cursor: 'pointer' }}
          >Close</button>
        </div>
      </div>

      <div className="node-inspector-body">
        {/* Specs — editable (these used to live only in the retired Table view). */}
        <div className="specs-body inspector-fields" key={activeProcess.id}>
          <div className="inspector-field">
            <label>Type</label>
            <input defaultValue={activeProcess.process_type || ''} placeholder="e.g. Mixing"
              onBlur={(e) => commitField('process_type', e.target.value)} onKeyDown={blurOnEnter} />
          </div>
          <div className="inspector-field">
            <label>Category</label>
            <input defaultValue={activeProcess.manufacturing_category || ''} placeholder="e.g. cathode"
              onBlur={(e) => commitField('manufacturing_category', e.target.value)} onKeyDown={blurOnEnter} />
          </div>
          <div className="inspector-field-row">
            <div className="inspector-field">
              <label>Duration (h)</label>
              <input type="number" step="any" defaultValue={activeProcess.parameters?.duration_hours ?? ''}
                onBlur={(e) => commitParam('duration_hours', e.target.value)} onKeyDown={blurOnEnter} />
            </div>
            <div className="inspector-field">
              <label>Temp (°C)</label>
              <input type="number" step="any" defaultValue={activeProcess.parameters?.temperature_c ?? ''}
                onBlur={(e) => commitParam('temperature_c', e.target.value)} onKeyDown={blurOnEnter} />
            </div>
          </div>
          <div className="inspector-field">
            <label>Equipment</label>
            <input defaultValue={activeProcess.equipment || ''} placeholder="optional"
              onBlur={(e) => commitField('equipment', e.target.value)} onKeyDown={blurOnEnter} />
          </div>
          <div className="inspector-field">
            <label>Source / notes</label>
            <input defaultValue={activeProcess.description || ''} placeholder="optional"
              onBlur={(e) => commitField('description', e.target.value)} onKeyDown={blurOnEnter} />
          </div>
        </div>

        <div style={{ padding: '0 12px 12px' }}>
          {/* Outputs */}
          {hasOutputs && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Outputs</div>
              {outputs.map((product, index) => {
                const editKey = `${activeProcess.id}::${index}`;
                const isEditingPrice = editingOutputPrice === editKey;
                const hasPrice = Number(product.price_per_unit) > 0;
                const updatePrice = (rawValue) => {
                  const value = Number(rawValue);
                  const list = activeProcess.products ? [...activeProcess.products] : [...(activeProcess.output_materials || [])];
                  const next = { ...list[index] };
                  if (Number.isFinite(value) && value > 0) next.price_per_unit = value;
                  else delete next.price_per_unit;
                  list[index] = next;
                  const updateField = activeProcess.products ? 'products' : 'output_materials';
                  handleUpdateProcess(activeProcess.id, { [updateField]: list });
                };
                return (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
                    <span style={{ color: '#333', flex: 1 }}>{product.material?.replace(/_/g, ' ')}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, padding: '2px 8px', fontSize: '13px', minWidth: '50px', textAlign: 'right', display: 'inline-block' }}>
                        {fmt3(product.quantity)}
                      </span>
                      <span style={{ color: '#888', fontSize: '12px', minWidth: '30px' }}>{product.unit}</span>
                      {isEditingPrice ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <span style={{ color: '#2e7d32', fontSize: '12px' }}>$</span>
                          <input
                            type="number"
                            defaultValue={product.price_per_unit ?? ''}
                            onBlur={(e) => { updatePrice(e.target.value); setEditingOutputPrice(null); }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { updatePrice(e.target.value); setEditingOutputPrice(null); }
                              if (e.key === 'Escape') { setEditingOutputPrice(null); }
                            }}
                            autoFocus
                            placeholder="price"
                            style={{ width: '60px', padding: '2px 4px', border: '1px solid #2e7d32', borderRadius: 4, fontSize: '12px', textAlign: 'right' }}
                          />
                          <span style={{ color: '#888', fontSize: '11px' }}>/{product.unit}</span>
                        </span>
                      ) : (
                        <span
                          onClick={() => setEditingOutputPrice(editKey)}
                          title="Click to set selling price (drives revenue & profit in TEA)"
                          style={{ color: hasPrice ? '#2e7d32' : '#999', fontSize: '12px', minWidth: '60px', textAlign: 'right', cursor: 'pointer', fontStyle: hasPrice ? 'normal' : 'italic' }}
                        >
                          {hasPrice ? `$${fmt3(product.price_per_unit)}/${product.unit}` : '+ price'}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Inputs */}
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Inputs</span>
            {!isDemoBuild && <DatabaseModeToggle onModeChange={setTraciDbMode} />}
          </div>
          <LCIInputsTable
            processId={activeProcess.id}
            lciInputs={lciInputs}
            useNoisyDb={useNoisyDb}
            impactSource={impactSource}
            onImpactSourceChange={switchImpactSource}
            onInputsChange={(updatedLciInputs) => {
              if (handleUpdateProcess) handleUpdateProcess(activeProcess.id, { lci_inputs: updatedLciInputs });
            }}
          />
        </div>
      </div>
    </div>
  );
}
